/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {ColDef, ColGroupDef, GridApi, ValueFormatterParams} from 'ag-grid-community';
import {CommonHelperService} from './common-helper.service';
import {Workbook} from 'exceljs';
import {isSafeWebUrl} from '../utils/url.util';

interface GridExportColumn {
    col: ColDef;
    headerPath: string[];
}

interface GridExportSetup {
    exportColumns: GridExportColumn[];
    visibleCols: ColDef[];
    exportHeaderByColumnKey: Map<string, string>;
    headers: string[];
    headerPaths: string[][];
}

@Injectable({
  providedIn: 'root'
})
export class ExcelService {

    constructor() { }
    async exportDataAsExcelFile(data: any[], sheetName: string, fileName: string, orderedHeaders?: string[],
                                headerPaths?: string[][], shouldDownload: () => boolean = () => true) {
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet(ExcelService.sanitizeExcelSheetName(sheetName));

        const headers = orderedHeaders?.length ? orderedHeaders : Object.keys(data[0] ?? {});
        this.addHeaderRows(worksheet, headers, headerPaths);

        const convertCellData = (cellData: any, header: any, rowIndex: number) => {
            if (cellData == null) {
                return '';
            }
            if (cellData.hyperlink) {
                return {text: cellData.text, hyperlink: cellData.hyperlink};
            }
            // I tried to add images to the excel file, but it didn't work
            // if (cellData.imageUrl) {
            //     const imageId = workbook.addImage({
            //         filename: cellData.imageUrl,
            //         extension: 'png'
            //     });
            //     worksheet.addImage(imageId, {
            //         tl: { col: headers.indexOf(header), row: rowIndex + 1 },
            //         ext: { width: 50, height: 50 }
            //     });
            //     return ''; // Leave cell empty because the image is drawn above
            // }
            return cellData;
        };

        data.forEach(row => {
            const rowData = headers.map((header, rowIndex) => {
                let rowElement = row[header];
                if (Array.isArray(rowElement)) {
                    const hyperlinks = rowElement.filter(item => item?.hyperlink);
                    if (hyperlinks.length === 1) {
                        rowElement = hyperlinks[0];
                    } else if (hyperlinks.length > 1) {
                        return hyperlinks.map(item => item.hyperlink).join(' \n');
                    } else if (rowElement.length === 1) {
                        rowElement = rowElement[0];
                    } else if (rowElement.length === 0) {
                        return '';
                    } else {
                        return rowElement.map(item => item == null ? '' : (item.hyperlink ? item.hyperlink : item))
                                         .join(', ');
                    }
                }
                if (rowElement != null && typeof rowElement === 'object') {
                    return convertCellData(rowElement, header, rowIndex);
                }
                return rowElement;
            });
            worksheet.addRow(rowData);
        });

        worksheet.eachRow((row) => {
            row.eachCell((cell) => {
                if (cell.hyperlink) {
                    cell.font = { color: { argb: 'FF0000FF' }, underline: true };
                }
            });
        });

        const buffer = await workbook.xlsx.writeBuffer();
        if (!shouldDownload()) {
            return;
        }
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        CommonHelperService.downloadFile(blob, `${fileName}.xlsx`);
    }

    // Exports visible columns. Optionally accepts a distinct worksheet/tab name; defaults to fileName.
    async exportGridDataAsExcelFile(gridApi: GridApi, fileName: string, sheetName?: string) {
        // export all data in the grid
        const rowData = [];
        gridApi.forEachNodeAfterFilterAndSort(node => rowData.push(node.data));
        const setup = this.buildGridExportSetup(
            gridApi.getColumnDefs() || [],
            col => !!col.field && !this.isDerivedMultiSelectDisplayColumn(col));
        const data = structuredClone(rowData).map(row => {
            const projectedRow: Record<string, unknown> = {};
            for (const col of setup.visibleCols) {
                const exportHeader = setup.exportHeaderByColumnKey.get(this.getExportColumnKey(col)) ||
                    col.headerName || col.field;
                this.convertData(col, row, exportHeader);
                projectedRow[exportHeader] = Object.prototype.hasOwnProperty.call(row, exportHeader)
                    ? row[exportHeader] : '';
            }
            return projectedRow;
        });

        await this.exportDataAsExcelFile(data, sheetName ?? fileName,
                                         `${fileName}${CommonHelperService.getTimeStampForReport()}`,
                                         setup.headers);
    }

    async exportGridRowsAsExcelFile(rowData: any[], cols: (ColDef | ColGroupDef)[],
                                    fileName: string, sheetName?: string,
                                    shouldDownload: () => boolean = () => true) {
        const setup = this.buildGridExportSetup(cols);
        const data = structuredClone(rowData)
            .flatMap(row => this.expandGridRow(row, setup.visibleCols, setup.exportHeaderByColumnKey));

        await this.exportDataAsExcelFile(data, sheetName ?? fileName,
                                         `${fileName}${CommonHelperService.getTimeStampForReport()}`,
                                         setup.headers, setup.headerPaths, shouldDownload);
    }

    convertData(col: any | ColGroupDef, row: any, exportHeader?: string) {
        const fieldName = col.field;
        // if no member matches, skip
        if (fieldName || typeof col.valueGetter === 'function') {
            const cellData = this.convertResolvedCellData(col, this.resolveColumnValue(col, row), row);
            row[exportHeader || col.headerName || col.field] = cellData;
        }
    }

    private convertResolvedCellData(col: ColDef, resolvedValue: unknown,
                                    row: Record<string, unknown>): unknown {
        let cellData = resolvedValue;
        if (cellData === undefined || cellData === null) {
            return cellData;
        }
        if (col.field === 'partImageFileIds' && typeof cellData === 'object' && !Array.isArray(cellData)) {
            cellData = Object.values(cellData)
                             .filter(Array.isArray)
                             .flat();
        }
        if (Array.isArray(cellData)) {
            if (this.shouldExportArrayItemsIndividually(col)) {
                return cellData.map(item => this.convertCellData(col, item, row))
                               .filter(item => item !== null && item !== undefined && item !== '');
            }
            if (typeof col.valueFormatter === 'function') {
                // Apply formatter to the full array first so array-aware formatters work correctly.
                return col.valueFormatter({value: cellData} as ValueFormatterParams);
            }
            return cellData.map(item => this.convertCellData(col, item, row))
                           .filter(item => item !== null && item !== undefined && item !== '');
        }
        return this.convertCellData(col, cellData, row);
    }

    private getExportColumns(cols: (ColDef | ColGroupDef)[], parentHeaders: string[] = []): GridExportColumn[] {
        const exportColumns: GridExportColumn[] = [];
        for (const col of cols) {
            if ((col as ColGroupDef).children) {
                const group = col as ColGroupDef;
                const groupHeaders = group.headerName ? [...parentHeaders, group.headerName] : parentHeaders;
                exportColumns.push(...this.getExportColumns(
                    group.children as (ColDef | ColGroupDef)[], groupHeaders));
                continue;
            }
            const leafCol = col as ColDef;
            const hasValue = !!leafCol.field || typeof leafCol.valueGetter === 'function';
            if (leafCol.hide !== true && hasValue && !this.isSensitiveExportField(leafCol.field)) {
                exportColumns.push({
                    col: leafCol,
                    headerPath: [...parentHeaders, leafCol.headerName || leafCol.field || leafCol.colId || '']
                });
            }
        }
        return exportColumns;
    }

    private getExportColumnKey(col: ColDef): string {
        const persistKey = (col as {esciPersistKey?: string}).esciPersistKey;
        if (persistKey) {
            return persistKey;
        }
        return col.colId || col.field || col.headerName || '';
    }

    private isDerivedMultiSelectDisplayColumn(col: ColDef): boolean {
        const persistKey = (col as {esciPersistKey?: string}).esciPersistKey;
        return typeof persistKey === 'string' && persistKey.includes('::');
    }

    private isSensitiveExportField(field: string | undefined): boolean {
        const normalizedField = field?.toLowerCase().trim();
        return normalizedField === 'ppco' || !!normalizedField?.startsWith('ppco.');
    }

    private buildExportHeadersByColumnKey(cols: ColDef[]): Map<string, string> {
        const headerCounts = new Map<string, number>();
        const headersByColumnKey = new Map<string, string>();
        for (const col of cols) {
            const columnKey = this.getExportColumnKey(col);
            const fieldOrId = col.field || col.colId || col.headerName;
            if (!columnKey || !fieldOrId) {
                continue;
            }
            const baseHeader = col.headerName || fieldOrId;
            const count = headerCounts.get(baseHeader) || 0;
            headerCounts.set(baseHeader, count + 1);
            const disambiguator = col.colId && col.colId !== col.field ? col.colId : fieldOrId;
            const exportHeader = count === 0 ? baseHeader : `${baseHeader} (${disambiguator})`;
            headersByColumnKey.set(columnKey, exportHeader);
        }
        return headersByColumnKey;
    }

    private buildGridExportSetup(cols: (ColDef | ColGroupDef)[],
                                 includeColumn: (col: ColDef) => boolean = () => true): GridExportSetup {
        const exportColumns = this.getExportColumns(cols).filter(({col}) => includeColumn(col));
        const visibleCols = exportColumns.map(({col}) => col);
        const exportHeaderByColumnKey = this.buildExportHeadersByColumnKey(visibleCols);
        return {
            exportColumns,
            visibleCols,
            exportHeaderByColumnKey,
            headers: Array.from(exportHeaderByColumnKey.values()),
            headerPaths: exportColumns.map(({headerPath}) => headerPath)
        };
    }

    private shouldExportArrayItemsIndividually(col: ColDef): boolean {
        if (col.field === 'partImageFileIds') {
            return true;
        }
        const memberType = col.cellRendererParams?.memberType;
        return memberType === 'image_list'
            || (memberType === 'attachment' && !!col.cellRendererParams?.urlBase);
    }

    private resolveColumnValue(col: ColDef, row: any): any {
        const preferValueGetter = typeof col.valueGetter === 'function' &&
            ((col as any).esciDerivedGroupChildField === true ||
             String((col as any).esciPersistKey ?? '').includes('::') ||
             (col.field === 'partImageFileIds' && String(col.colId ?? '').startsWith('partImageFileIds__')) ||
             !col.field);
        let cellData = preferValueGetter
            ? (col.valueGetter as Function)({data: row})
            : CommonHelperService.getDeepValue(row, col.field || '');
        if ((cellData === undefined || cellData === null) && typeof col.valueGetter === 'function') {
            cellData = (col.valueGetter as Function)({data: row});
        }
        return cellData;
    }

    private expandGridRow(row: any, cols: ColDef[],
                          exportHeaderByColumnKey: Map<string, string>): Record<string, any>[] {
        const values = cols.map(col => {
            const value = this.resolveColumnValue(col, row);
            const collectionValues = this.isCollectionColumn(col)
                ? this.flattenCollectionValue(value)
                : null;
            return {
                col,
                collectionValues,
                scalarValue: collectionValues === null ? this.convertResolvedCellData(col, value, row) : null
            };
        });
        const rowCount = Math.max(1, ...values.map(value =>
            value.collectionValues === null ? 0 : value.collectionValues.length));
        const expandedRows: Record<string, any>[] = [];

        for (let rowIndex = 0; rowIndex < rowCount; rowIndex++) {
            const expandedRow: Record<string, any> = {};
            for (const value of values) {
                const header = exportHeaderByColumnKey.get(this.getExportColumnKey(value.col)) ||
                    value.col.headerName || value.col.field || value.col.colId || '';
                if (value.collectionValues !== null) {
                    const item = value.collectionValues[rowIndex];
                    expandedRow[header] = item === undefined || item === null
                        ? ''
                        : this.convertCellData(value.col, item, row, rowIndex);
                } else if (rowIndex === 0) {
                    expandedRow[header] = value.scalarValue;
                } else {
                    expandedRow[header] = '';
                }
            }
            expandedRows.push(expandedRow);
        }
        return expandedRows;
    }

    private isCollectionColumn(col: ColDef): boolean {
        const cellClasses = Array.isArray(col.cellClass) ? col.cellClass : [col.cellClass];
        return !!col.field?.includes('[]') ||
            cellClasses.includes('subrow-cell') ||
            (col as any).esciDerivedGroupChildField === true ||
            String((col as any).esciPersistKey ?? '').includes('::');
    }

    private flattenCollectionValue(value: any): any[] {
        if (value === undefined || value === null) {
            return [];
        }
        return Array.isArray(value) ? value.flat(Infinity) : [value];
    }

    private addHeaderRows(worksheet: any, headers: string[], headerPaths?: string[][]): void {
        if (!headerPaths?.length || headerPaths.every(path => path.length <= 1)) {
            worksheet.addRow(headers);
            return;
        }
        const paths = headers.map((header, index) => {
            const path = (headerPaths[index] || []).filter(part => !!part);
            return path.length ? path : [header];
        });
        const depth = Math.max(...paths.map(path => path.length));
        for (let rowIndex = 0; rowIndex < depth; rowIndex++) {
            worksheet.addRow(new Array(headers.length).fill(''));
        }

        paths.forEach((path, columnIndex) => {
            const groups = path.slice(0, -1);
            groups.forEach((group, groupIndex) => {
                worksheet.getCell(groupIndex + 1, columnIndex + 1).value = group;
            });
            const leafStartRow = groups.length + 1;
            worksheet.getCell(leafStartRow, columnIndex + 1).value = path[path.length - 1];
            if (leafStartRow < depth) {
                worksheet.mergeCells(leafStartRow, columnIndex + 1, depth, columnIndex + 1);
            }
        });

        for (let groupDepth = 0; groupDepth < depth - 1; groupDepth++) {
            let start = 0;
            while (start < paths.length) {
                const groups = paths[start].slice(0, -1);
                if (groups.length <= groupDepth) {
                    start++;
                    continue;
                }
                const prefix = groups.slice(0, groupDepth + 1).join('\u0000');
                let end = start + 1;
                while (end < paths.length) {
                    const nextGroups = paths[end].slice(0, -1);
                    if (nextGroups.length <= groupDepth ||
                        nextGroups.slice(0, groupDepth + 1).join('\u0000') !== prefix) {
                        break;
                    }
                    end++;
                }
                if (end - start > 1) {
                    worksheet.mergeCells(groupDepth + 1, start + 1, groupDepth + 1, end);
                }
                start = end;
            }
        }
    }

    convertCellData(col: any | ColGroupDef, cellData: any, row: any, collectionIndex?: number): any {
        // For user objects that are empty, return an empty string
        // This used to check for col.cellRenderer.name === UserSearchCellEditorComponent
        // but that approach is fragile, now we check for "userField"
        if (col.cellRendererParams && col.cellRendererParams.userField &&
            !cellData.fullName && !cellData.firstName) {
            cellData = '';
        } else {
            // otherwise, try converting to real full name from a user object
            cellData = this.checkAndReturnUserNameIfUser(cellData);
        }
        if (col.valueFormatter && !(this.shouldExportArrayItemsIndividually(col) && !Array.isArray(cellData))) {
            cellData = col.valueFormatter({value: cellData});
        }

        // See if the cellData is a URL and if so, use the URL as a hyperlink
        if ((col.field === 'partImageFileId') && row.partImageUrl) {
            return { text: 'Image ' + cellData, hyperlink: row.partImageUrl };
        }
        if ((col.field === 'attachmentFileId') && row.attachmentUrl) {
            return { text: row.attachmentName || 'Attachment', hyperlink: row.attachmentUrl };
        }
        const isValidationReportFile = col.field === 'validationReportFileId' ||
            col.field?.endsWith('.validationReportFileId') ||
            col.field?.endsWith('].validationReportFileId');
        const attachmentParams = col.cellRendererParams || col.cellEditorParams;
        if (isValidationReportFile && attachmentParams?.urlBase && cellData > 0) {
            const compId = row.id > 0 ? row.id : row.originalRowData?.id;
            if (compId > 0) {
                const url = `${attachmentParams.urlBase}/${compId}/${cellData}`;
                const nameField = attachmentParams?.nameField;
                const displayName = nameField
                    ? this.getSiblingFieldValue(row, col.field, nameField, collectionIndex) : null;
                return { text: String(displayName || cellData), hyperlink: url };
            }
        }
        if (col.field === 'partImageFileIds' && col.cellRendererParams?.urlBase) {
            const compId = row.id > 0 ? row.id : row.originalRowData?.id;
            if (!(compId > 0) || !(cellData > 0)) {
                return '';
            }
            const url = `${col.cellRendererParams.urlBase}/${compId}/${cellData}.png`;
            return { text: `Image ${cellData}`, hyperlink: url };
        }
        if (col.cellRendererParams?.useValueAsUrl && cellData) {
            const urlText = String(cellData);
            return isSafeWebUrl(urlText) ? { text: urlText, hyperlink: urlText } : urlText;
        }

        return this.checkAndFixNanDate(cellData);
    }

    private getSiblingFieldValue(row: any, field: string, siblingField: string,
                                 collectionIndex?: number): any {
        const collectionMarker = field?.indexOf('[]') ?? -1;
        if (collectionMarker < 0) {
            const fieldPrefix = field?.includes('.')
                ? field.substring(0, field.lastIndexOf('.') + 1) : '';
            return CommonHelperService.getDeepValue(row, fieldPrefix + siblingField);
        }

        const collection = CommonHelperService.getDeepValue(row, field.substring(0, collectionMarker));
        if (!Array.isArray(collection)) {
            return null;
        }
        const entry = collection[collectionIndex ?? 0];
        const itemField = field.substring(collectionMarker + 2).replace(/^\./, '');
        const itemPrefix = itemField.includes('.')
            ? itemField.substring(0, itemField.lastIndexOf('.') + 1) : '';
        return CommonHelperService.getDeepValue(entry, itemPrefix + siblingField);
    }

    checkAndReturnUserNameIfUser(user: any) {
        // if an object with a name member, use name
        if (user instanceof Object && user.firstName) {
            return user.firstName + ' ' + user.lastName;
        }
        if (user instanceof Object && user.fullName) {
            return user.fullName;
        }
        return user;
    }

    checkAndFixNanDate(date: any) {
        // if value is a date, prevent the NaN from corrupting the excel file
        if (date instanceof Date) {
            return isNaN(date.getTime()) ? null : date;
        }
        return date;
    }

    /** Excel worksheet names: max 31 chars; cannot contain []:*?/\\ */
    static sanitizeExcelSheetName(sheetName: string | undefined | null): string {
        const raw = String(sheetName ?? '').trim() || 'Sheet1';
        const sanitized = raw.replace(/[\[\]:*?/\\]/g, '_');
        return sanitized.length > 31 ? sanitized.substring(0, 31) : sanitized;
    }
}
